// src/components/AddTransactionForm.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./AddTransactionForm.css";
import categories from "../data/categories";
import { API_BASE } from "../config";

/* ---------------- Helpers ---------------- */

async function readPayloadSafe(res) {
  if (!res) return {};
  if (res.status === 204) return {};
  const ct = res.headers.get("content-type") || "";
  try {
    if (ct.includes("application/json")) return await res.json();
    const text = await res.text();
    return text ? { message: text } : {};
  } catch {
    return {};
  }
}

function pickErrorMessage(payload, fallback) {
  if (payload && typeof payload === "object") {
    return payload.error || payload.message || payload.detail || fallback;
  }
  return fallback;
}

function todayIso() {
  return new Date().toISOString().split("T")[0];
}

function normalizeText(s) {
  return String(s || "").toLowerCase().trim();
}

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function bytesToHuman(n) {
  const b = Number(n || 0);
  if (!Number.isFinite(b) || b <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.min(units.length - 1, Math.floor(Math.log(b) / Math.log(1024)));
  const v = b / Math.pow(1024, i);
  return `${v.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

/** Timeout wrapper (fetch) */
async function fetchWithTimeout(url, options = {}, timeoutMs = 15000) {
  const controller = new AbortController();
  const t = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const res = await fetch(url, { ...options, signal: controller.signal });
    return res;
  } finally {
    clearTimeout(t);
  }
}

/* ---------------- Component ---------------- */

export default function AddTransactionForm({ onAdd, token }) {
  const [data, setData] = useState(todayIso());
  const [descrizione, setDescrizione] = useState("");
  const [importo, setImporto] = useState("");
  const [tipo, setTipo] = useState("Entrata");
  const [categoria, setCategoria] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("Bonifico");

  // Receipt
  const [receiptData, setReceiptData] = useState(null); // dataURL
  const [receiptName, setReceiptName] = useState("");
  const [receiptSize, setReceiptSize] = useState(0);

  // UI submit
  const [saving, setSaving] = useState(false);

  // Suppliers
  const [suppliers, setSuppliers] = useState([]);
  const [supplierQuery, setSupplierQuery] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [supplierLoading, setSupplierLoading] = useState(false);
  const [supplierError, setSupplierError] = useState("");

  // Command palette UI state
  const [supplierOpen, setSupplierOpen] = useState(false);
  const [supplierActiveIndex, setSupplierActiveIndex] = useState(0);

  // Modal “nuovo fornitore”
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [newSupplierName, setNewSupplierName] = useState("");
  const [newSupplierEmail, setNewSupplierEmail] = useState("");
  const [newSupplierPhone, setNewSupplierPhone] = useState("");

  // refs
  const supplierWrapRef = useRef(null);
  const supplierInputRef = useRef(null);
  const paymentSelectRef = useRef(null);
  const fileInputRef = useRef(null);

  const focusPayment = () => setTimeout(() => paymentSelectRef.current?.focus?.(), 0);

  // ✅ Limite receipt (base64 in JSON): tienilo basso per evitare 413
  // (base64 aumenta dimensioni ~33%, quindi meglio limitare il FILE)
  const MAX_RECEIPT_BYTES = 2 * 1024 * 1024; // 2MB

  // Filtra categorie per tipo
  const filteredCategories = useMemo(() => {
    const arr = Array.isArray(categories) ? categories : [];
    return tipo === "Entrata" ? arr.filter((c) => c.type === "Entrata") : arr.filter((c) => c.type === "Uscita");
  }, [tipo]);

  // preselect categoria
  useEffect(() => {
    if (!categoria && filteredCategories.length) setCategoria(filteredCategories[0].name);
  }, [filteredCategories, categoria]);

  // Headers auth
  const authHeaders = useMemo(() => {
    const h = { "Content-Type": "application/json", Accept: "application/json" };
    if (token) h.Authorization = `Bearer ${token}`;
    return h;
  }, [token]);

  // helper: mobile detection (matchMedia)
  const isMobile = useMemo(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia?.("(max-width: 560px)")?.matches ?? false;
  }, []);

  /* ---------------- Suppliers fetch ---------------- */

  const fetchSuppliers = useCallback(async () => {
    if (!token) return;

    setSupplierLoading(true);
    setSupplierError("");

    try {
      const res = await fetchWithTimeout(`${API_BASE}/api/suppliers`, { method: "GET", headers: authHeaders }, 15000);
      const payload = await readPayloadSafe(res);

      if (!res.ok) {
        if (res.status === 401) {
          setSuppliers([]);
          setSupplierOpen(false);
          setSupplierError("🔒 Sessione scaduta o permessi insufficienti per visualizzare i fornitori.");
          return;
        }

        console.error("Suppliers error", res.status, payload);
        throw new Error(pickErrorMessage(payload, "Errore nel caricamento fornitori."));
      }

      const arr = Array.isArray(payload) ? payload : [];
      arr.sort((a, b) => String(a?.name || "").localeCompare(String(b?.name || ""), "it"));
      setSuppliers(arr);
    } catch (e) {
      const msg =
        e?.name === "AbortError"
          ? "⏳ Timeout: server fornitori non raggiungibile."
          : e?.message || "Errore nel caricamento fornitori.";
      setSupplierError(msg);
    } finally {
      setSupplierLoading(false);
    }
  }, [token, authHeaders]);

  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  const filteredSuppliers = useMemo(() => {
    const q = normalizeText(supplierQuery);
    if (!q) return suppliers;
    return suppliers.filter((s) => {
      const name = normalizeText(s?.name);
      const email = normalizeText(s?.email);
      const phone = normalizeText(s?.phone);
      return name.includes(q) || email.includes(q) || phone.includes(q);
    });
  }, [suppliers, supplierQuery]);

  const selectedSupplier = useMemo(() => {
    if (!supplierId) return null;
    return suppliers.find((s) => String(s.id) === String(supplierId)) || null;
  }, [suppliers, supplierId]);

  // Quando cambia il filtro, reset highlight
  useEffect(() => {
    setSupplierActiveIndex(0);
  }, [supplierQuery]);

  /* ---------------- Close behavior: outside + ESC ---------------- */

  useEffect(() => {
    if (!supplierOpen) return;

    const onKey = (e) => {
      if (e.key === "Escape") setSupplierOpen(false);
    };

    const onDown = (e) => {
      const root = supplierWrapRef.current;
      if (!root) return;
      if (!root.contains(e.target)) setSupplierOpen(false);
    };

    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onDown);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onDown);
    };
  }, [supplierOpen]);

  // se la bottom-sheet è aperta su mobile, evita scroll del body
  useEffect(() => {
    if (!supplierOpen || !isMobile) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [supplierOpen, isMobile]);

  const selectSupplier = (s) => {
    setSupplierId(String(s?.id || ""));
    setSupplierQuery(String(s?.name || ""));
    setSupplierOpen(false);
    focusPayment();
    supplierInputRef.current?.blur?.();
  };

  const clearSupplier = () => {
    setSupplierId("");
    setSupplierQuery("");
    setSupplierActiveIndex(0);
    setSupplierOpen(true);
    setTimeout(() => supplierInputRef.current?.focus?.(), 0);
  };

  const handleSupplierKeyDown = (e) => {
    if (!supplierOpen) {
      if (e.key === "ArrowDown" || e.key === "Enter") setSupplierOpen(true);
      return;
    }

    const list = filteredSuppliers;
    if (!list.length) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSupplierActiveIndex((i) => clamp(i + 1, 0, list.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSupplierActiveIndex((i) => clamp(i - 1, 0, list.length - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const target = list[supplierActiveIndex];
      if (target) selectSupplier(target);
    }
  };

  /* ---------------- Receipt ---------------- */

  const clearReceipt = () => {
    setReceiptData(null);
    setReceiptName("");
    setReceiptSize(0);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      clearReceipt();
      return;
    }

    // ✅ blocca prima del base64 per evitare 413
    if (file.size > MAX_RECEIPT_BYTES) {
      clearReceipt();
      alert(
        `📦 File troppo grande (${bytesToHuman(file.size)}).\n` +
          `Limite attuale: ${bytesToHuman(MAX_RECEIPT_BYTES)}.\n\n` +
          `Consiglio: comprimi l’immagine o salva il PDF più leggero 🙂`
      );
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setReceiptData(reader.result);
      setReceiptName(file.name);
      setReceiptSize(file.size);
    };
    reader.onerror = () => {
      clearReceipt();
      alert("⚠️ Non riesco a leggere il file. Riprova.");
    };
    reader.readAsDataURL(file);
  };

  /* ---------------- Create supplier ---------------- */

  const openSupplierModal = () => {
    setSupplierError("");
    setNewSupplierName("");
    setNewSupplierEmail("");
    setNewSupplierPhone("");
    setShowSupplierModal(true);
    setSupplierOpen(false);
  };

  const closeSupplierModal = () => setShowSupplierModal(false);

  const createSupplier = async () => {
    if (!token) return;

    const name = String(newSupplierName || "").trim();
    if (!name) {
      setSupplierError("✍️ Inserisci il nome del fornitore.");
      return;
    }

    setSupplierError("");
    try {
      const res = await fetchWithTimeout(
        `${API_BASE}/api/suppliers`,
        {
          method: "POST",
          headers: authHeaders,
          body: JSON.stringify({
            name,
            email: String(newSupplierEmail || "").trim() || null,
            phone: String(newSupplierPhone || "").trim() || null,
          }),
        },
        15000
      );

      const created = await readPayloadSafe(res);

      if (!res.ok) {
        if (res.status === 401) {
          setSupplierError("🔒 Sessione scaduta o permessi insufficienti per creare fornitori.");
          return;
        }
        throw new Error(pickErrorMessage(created, "Errore creazione fornitore."));
      }

      const next = [...suppliers, created].sort((a, b) =>
        String(a?.name || "").localeCompare(String(b?.name || ""), "it")
      );

      setSuppliers(next);
      setSupplierId(String(created.id));
      setSupplierQuery(String(created.name || ""));
      setShowSupplierModal(false);
      setSupplierOpen(false);

      focusPayment();
    } catch (e) {
      const msg =
        e?.name === "AbortError" ? "⏳ Timeout: API non raggiungibile." : e?.message || "Errore creazione fornitore.";
      setSupplierError(msg);
    }
  };

  /* ---------------- Submit transaction ---------------- */

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      alert("🔒 Devi effettuare l’accesso.");
      return;
    }
    if (saving) return;

    const amount = parseFloat(importo);
    if (Number.isNaN(amount)) {
      alert("💶 Importo non valido.");
      return;
    }

    // ✅ guardrail: base64 può esplodere, quindi doppio controllo
    if (receiptData && receiptSize > MAX_RECEIPT_BYTES) {
      alert(`📦 Ricevuta troppo grande. Limite: ${bytesToHuman(MAX_RECEIPT_BYTES)}.`);
      return;
    }

    const catObj = (Array.isArray(categories) ? categories : []).find((c) => c.name === categoria);

    const newTransactionPayload = {
      data: data || todayIso(),
      descrizione: String(descrizione || "").trim(),
      importo: tipo === "Entrata" ? amount : -amount,
      tipo,
      categoria,
      coloreCategoria: catObj ? catObj.color : undefined,
      paymentMethod,

      supplierId: supplierId || null,
      supplierName: selectedSupplier?.name || null,

      // ⚠️ base64 in JSON: ok solo per file piccoli
      receipt: receiptData ? { data: receiptData, name: receiptName } : null,
    };

    setSaving(true);

    try {
      const res = await fetchWithTimeout(
        `${API_BASE}/api/transactions`,
        {
          method: "POST",
          headers: authHeaders,
          body: JSON.stringify(newTransactionPayload),
        },
        20000
      );

      // ✅ 413: messaggio chiaro (niente Invalid JSON)
      if (res.status === 413) {
        throw new Error(
          "📦 Allegato troppo grande per il server (413). Riduci la ricevuta (es. < 2MB) oppure caricala con upload separato."
        );
      }

      const saved = await readPayloadSafe(res);

      if (!res.ok) {
        if (res.status === 401) throw new Error("🔒 Sessione scaduta. Fai login e riprova.");
        throw new Error(pickErrorMessage(saved, `Errore nel salvataggio (HTTP ${res.status}).`));
      }

      if (typeof onAdd === "function") onAdd(saved);

      // Reset ✅
      setData(todayIso());
      setDescrizione("");
      setImporto("");
      setTipo("Entrata");
      setCategoria(filteredCategories[0]?.name || "");
      setPaymentMethod("Bonifico");
      clearReceipt();

      setSupplierId("");
      setSupplierQuery("");
      setSupplierOpen(false);
      setSupplierActiveIndex(0);
      setSupplierError("");
    } catch (err) {
      console.error("Errore nel salvataggio", err);
      const msg =
        err?.name === "AbortError"
          ? "⏳ Timeout: API non raggiungibile. Riprova tra poco."
          : err?.message || "Errore nel salvataggio della transazione.";
      alert(msg);
    } finally {
      setSaving(false);
    }
  };

  /* ---------------- UI ---------------- */

  return (
    <form className="add-transaction-form" onSubmit={handleSubmit}>
      <div className="atf-header">
        <h3 className="atf-title">➕ Aggiungi Transazione</h3>
        <span className="atf-hint">Compila i campi e salva ✨</span>
      </div>

      <div className="form-row">
        <label>📅 Data</label>
        <input type="date" value={data} onChange={(e) => setData(e.target.value)} required />
      </div>

      <div className="form-row">
        <label>🔁 Tipo</label>
        <select value={tipo} onChange={(e) => setTipo(e.target.value)} required>
          <option value="Entrata">Entrata</option>
          <option value="Uscita">Uscita</option>
        </select>
      </div>

      <div className="form-row">
        <label>📝 Descrizione</label>
        <input
          type="text"
          value={descrizione}
          onChange={(e) => setDescrizione(e.target.value)}
          required
          placeholder="Es. Fattura, donazione, acquisto…"
        />
      </div>

      <div className="form-row">
        <label>🏷️ Categoria</label>
        <select value={categoria} onChange={(e) => setCategoria(e.target.value)} required>
          {filteredCategories.map((cat) => (
            <option key={cat.name} value={cat.name}>
              {cat.name}
            </option>
          ))}
        </select>
      </div>

      {/* ✅ FORNITORE */}
      <div className="form-row">
        <label>🏢 Fornitore</label>

        <div
          className="spx"
          ref={supplierWrapRef}
          onBlur={(e) => {
            const root = supplierWrapRef.current;
            if (!root) return;
            if (!root.contains(e.relatedTarget)) setSupplierOpen(false);
          }}
        >
          <div className={`spx__control ${supplierOpen ? "is-open" : ""}`}>
            <div className="spx__icon" aria-hidden>
              🧾
            </div>

            <input
              ref={supplierInputRef}
              type="text"
              value={supplierQuery}
              onChange={(e) => {
                setSupplierQuery(e.target.value);
                setSupplierOpen(true);
              }}
              onFocus={() => setSupplierOpen(true)}
              onClick={() => setSupplierOpen(true)}
              onKeyDown={handleSupplierKeyDown}
              placeholder="Cerca fornitore (nome, email, telefono)…"
              disabled={!token || supplierLoading}
              aria-label="Cerca fornitore"
              inputMode="search"
              autoComplete="off"
            />

            {!!supplierQuery && (
              <button type="button" className="spx__clear" onClick={clearSupplier} aria-label="Svuota" title="Svuota">
                ✕
              </button>
            )}

            <button
              type="button"
              className="spx__pill"
              onClick={openSupplierModal}
              disabled={!token}
              title="Crea un nuovo fornitore"
            >
              + Nuovo
            </button>
          </div>

          {supplierError && (
            <div className="spx__alert" role="alert">
              {supplierError}
            </div>
          )}

          {supplierOpen && (
            <>
              <button
                type="button"
                className="spx__backdrop"
                aria-label="Chiudi selettore fornitore"
                onClick={() => setSupplierOpen(false)}
              />

              <div className="spx__popover" role="dialog" aria-label="Seleziona fornitore">
                <div className="spx__meta">
                  <div className="spx__metaLeft">Fornitori</div>

                  <div className="spx__metaRight">
                    <button type="button" className="spx__close" onClick={() => setSupplierOpen(false)}>
                      Chiudi
                    </button>
                    <span className="spx__kbdhint">↑ ↓ · Invio · Esc</span>
                  </div>
                </div>

                <div className="spx__list">
                  {supplierLoading ? (
                    <div className="spx__empty">
                      <div className="spx__emptyTitle">Caricamento…</div>
                      <div className="spx__emptyText">Sto recuperando l’elenco fornitori.</div>
                    </div>
                  ) : filteredSuppliers.length === 0 ? (
                    <div className="spx__empty">
                      <div className="spx__emptyTitle">Nessun risultato</div>
                      <div className="spx__emptyText">Prova a cambiare ricerca oppure crea un nuovo fornitore.</div>
                    </div>
                  ) : (
                    filteredSuppliers.map((s, idx) => {
                      const active = idx === supplierActiveIndex;
                      const selected = String(s.id) === String(supplierId);

                      return (
                        <button
                          key={s.id}
                          type="button"
                          className={`spx__item ${active ? "is-active" : ""} ${selected ? "is-selected" : ""}`}
                          onMouseEnter={() => setSupplierActiveIndex(idx)}
                          onClick={() => selectSupplier(s)}
                        >
                          <div className="spx__itemMain">
                            <div className="spx__name">
                              {selected && <span className="spx__check">✓</span>}
                              {s.name || "—"}
                            </div>
                            <div className="spx__sub">
                              {s.email ? s.email : "—"}
                              {s.phone ? ` · ${s.phone}` : ""}
                            </div>
                          </div>

                          <div className="spx__kbd" aria-hidden>
                            Invio
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>

                <div className="spx__footer">
                  <button type="button" className="spx__ghost" onClick={fetchSuppliers} disabled={!token || supplierLoading}>
                    {supplierLoading ? "Carico…" : "Aggiorna"}
                  </button>

                  <button type="button" className="spx__create" onClick={openSupplierModal} disabled={!token}>
                    + Nuovo fornitore
                  </button>
                </div>
              </div>
            </>
          )}

          {!!supplierId && selectedSupplier && (
            <div className="spx__selectedRow">
              <span className="spx__chip">
                ✅ {selectedSupplier.name}
                {selectedSupplier.email ? ` · ${selectedSupplier.email}` : ""}
              </span>

              <button type="button" className="spx__unlink" onClick={clearSupplier}>
                Rimuovi
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="form-row">
        <label>💳 Metodo di pagamento</label>
        <select
          ref={paymentSelectRef}
          value={paymentMethod}
          onChange={(e) => setPaymentMethod(e.target.value)}
          required
          onFocus={() => setSupplierOpen(false)}
        >
          <option value="Bonifico">Bonifico</option>
          <option value="Contanti">Contanti</option>
          <option value="Pagamento Elettronico">Pagamento Elettronico</option>
        </select>
      </div>

      <div className="form-row">
        <label>💶 Importo (€)</label>
        <input
          type="number"
          step="0.01"
          value={importo}
          onChange={(e) => setImporto(e.target.value)}
          required
          placeholder="0,00"
          inputMode="decimal"
          onFocus={() => setSupplierOpen(false)}
        />
      </div>

      <div className="form-row">
        <label>📎 Ricevuta di pagamento</label>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,application/pdf"
          onChange={handleFileChange}
          onFocus={() => setSupplierOpen(false)}
        />

        {receiptName ? (
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <small>
              ✅ File: <b>{receiptName}</b> ({bytesToHuman(receiptSize)})
              {" · "}
              <span style={{ opacity: 0.8 }}>limite {bytesToHuman(MAX_RECEIPT_BYTES)}</span>
            </small>

            <button type="button" className="atf-linkBtn" onClick={clearReceipt} title="Rimuovi ricevuta">
              🗑️ Rimuovi
            </button>
          </div>
        ) : (
          <small style={{ opacity: 0.8 }}>
            ℹ️ Consiglio: mantieni la ricevuta sotto <b>{bytesToHuman(MAX_RECEIPT_BYTES)}</b> per evitare errori 413.
          </small>
        )}
      </div>

      <div className="atf-actions">
        <button type="submit" className="atf-btnPrimary" disabled={saving}>
          {saving ? "⏳ Salvo…" : "✅ Aggiungi"}
        </button>
      </div>

      {/* ✅ MODAL: nuovo fornitore */}
      {showSupplierModal && (
        <div className="atf-modalOverlay" role="dialog" aria-modal="true" aria-label="Nuovo fornitore">
          <div className="atf-modal">
            <div className="atf-modalHeader">
              <div>
                <div className="atf-modalTitle">🏢 Nuovo fornitore</div>
                <div className="atf-modalSub">Crealo e lo selezioniamo subito ✨</div>
              </div>
              <button type="button" className="atf-iconBtn" onClick={closeSupplierModal} aria-label="Chiudi">
                ✕
              </button>
            </div>

            <div className="atf-modalBody">
              {supplierError && (
                <div className="atf-alert atf-alert--error" role="alert">
                  {supplierError}
                </div>
              )}

              <div className="form-row">
                <label>Nome</label>
                <input
                  type="text"
                  value={newSupplierName}
                  onChange={(e) => setNewSupplierName(e.target.value)}
                  placeholder="Es. Rossi Srl"
                  required
                />
              </div>

              <div className="form-row">
                <label>Email (opzionale)</label>
                <input
                  type="email"
                  value={newSupplierEmail}
                  onChange={(e) => setNewSupplierEmail(e.target.value)}
                  placeholder="es. amministrazione@rossi.it"
                  inputMode="email"
                />
              </div>

              <div className="form-row">
                <label>Telefono (opzionale)</label>
                <input
                  type="tel"
                  value={newSupplierPhone}
                  onChange={(e) => setNewSupplierPhone(e.target.value)}
                  placeholder="es. 3331234567"
                  inputMode="tel"
                />
              </div>
            </div>

            <div className="atf-modalFooter">
              <button type="button" className="atf-pillBtn atf-pillBtn--ghost" onClick={closeSupplierModal}>
                Annulla
              </button>
              <button type="button" className="atf-pillBtn" onClick={createSupplier}>
                ✅ Salva fornitore
              </button>
            </div>
          </div>
        </div>
      )}
    </form>
  );
}
